// src/ui/state.ts
// THE LITTLE BIT OF SELECTION STATE THE SCREENS SHARE, AND THE RULE THAT GOVERNS IT.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// AN ATHLETE ID MAY ONLY EVER COME FROM A SERVER RESPONSE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The brief: "Never send an athlete id from a body the server did not give you." This module is
// where that is enforced, because it is the only way an athlete id reaches a request in this app.
//
// `selectAthlete()` takes a `Provenance` — the response that produced the id. There is no overload
// that takes a bare string, so a screen cannot select an athlete it typed, guessed, read out of a
// URL, or carried over from another academy. The two legal provenances are exactly the two surfaces
// that enumerate athletes for a caller and gate each one:
//
//   · `roster_candidates` — GET …/identity/candidates, the academy roster
//   · `parent_children`   — GET /v1/practice/parent/children, a parent's own children
//
// The server re-gates every request anyway; this does not add security. What it adds is that a bug
// of this shape cannot be WRITTEN here, and so cannot be the thing that puts one family's child id
// into another family's request.

export type Provenance = 'roster_candidates' | 'parent_children';

export interface SelectedAthlete {
  readonly athleteId: string;
  /** Which server response handed us this id. */
  readonly from: Provenance;
  /** The academy it was enumerated under, when it came from a roster. */
  readonly academyId: string | null;
  readonly label: string;
  readonly subjectIsMinor: boolean | null;
}

export interface AppState {
  academyId: string | null;
  athlete: SelectedAthlete | null;
  /** The practice session a capture will be bound to. Server-issued id, never invented. */
  sessionId: string | null;
}

const state: AppState = { academyId: null, athlete: null, sessionId: null };
const watchers = new Set<(s: AppState) => void>();

export function getState(): Readonly<AppState> {
  return state;
}

export function onStateChange(fn: (s: AppState) => void): () => void {
  watchers.add(fn);
  return () => watchers.delete(fn);
}

function emit(): void {
  for (const fn of watchers) fn(state);
}

/**
 * The academy id IS typed by a coach, and that is fine: it is not an identity claim. The server
 * checks that this caller owns the academy before it returns a single roster row, and answers 403 or
 * 404 otherwise. A wrong academy id yields a refusal, not someone else's children.
 */
export function setAcademy(id: string | null): void {
  state.academyId = id && id.trim() ? id.trim() : null;
  if (state.athlete && state.athlete.academyId !== state.academyId) {
    // Selecting a new academy drops the athlete selected under the old one. Carrying it across
    // would be exactly the "id the server did not give you for THIS context" mistake.
    state.athlete = null;
    state.sessionId = null;
  }
  emit();
}

/** The ONLY way an athlete becomes selected. `from` is required, so provenance cannot be omitted. */
export function selectAthlete(a: SelectedAthlete): void {
  state.athlete = a;
  state.sessionId = null; // a session belongs to one athlete; never carry it across a change
  emit();
}

export function clearAthlete(): void {
  state.athlete = null;
  state.sessionId = null;
  emit();
}

/** A session id is only ever set from a server response (create, resume, or list). */
export function setSession(id: string | null): void {
  state.sessionId = id;
  emit();
}
